import * as React from 'react';

import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import { sanitizeGenericHtml } from 'utils/sanitize';

import './ccm-campaign-details-overview-budget.scss';

applyTranslations(require('./ccm-campaign-details-overview-budget.i18n.json'));

import type { Campaign } from 'types';

type Props = {
  campaign: Campaign,
  isCampaignCreatorless?: boolean
};

const MSG_KEY = 'ccm_campaign_details_overview_budget';

const CcmCampaignDetailsOverviewBudget: React.FC<Props> = ({
  campaign,
  isCampaignCreatorless
}): JSX.Element => {
  const outfitsRange = campaign.outfitsRequiredRange
    ? `${campaign.outfitsRequiredRange.min} to ${campaign.outfitsRequiredRange.max}`
    : '-';
  const getPopoverText = (): string => (`
    <div>
      ${__t(`${MSG_KEY}.popovers.budget.promotion`)}
    </div>
    <div>
      ${__t(`${MSG_KEY}.popovers.budget.production`)}
    </div>
  `);

  const renderTitle = (
    title: string
  ): React.ReactNode => (
    <div className="ccm-campaign-details-overview-budget__budget-title">
      {title}
    </div>
  );

  const renderBudgetItem = (
    value: string | number | undefined | null,
    description: string
  ): React.ReactNode => (
    <div className="ccm-campaign-details-overview-budget__budget-value">
      <div className="ccm-campaign-details-overview-budget__budget-value-amount">
        {value}
      </div>
      <div
        className="ccm-campaign-details-overview-budget__budget-value-description"
        dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(description) }}
      />
    </div>
  );

  const renderCampaignPackage = (): React.ReactNode => (
    <div className="ccm-campaign-details-overview-budget__budget-section">
      {renderTitle(__t(`${MSG_KEY}.sections.campaign_package.title`))}
      <div className="ccm-campaign-details-overview-budget__budget-values">
        {renderBudgetItem((campaign.packageName || '-').toUpperCase(), __t(`${MSG_KEY}.sections.campaign_package.package_type`))}
        <div className="ccm-campaign-details-overview-budget__budget-separator" />
        {renderBudgetItem(
          `${outfitsRange} outfits`,
          __t(`${MSG_KEY}.sections.campaign_package.required_outfits_live`)
        )}
        {renderBudgetItem(
          campaign.liveOutfitsCount ?? 0,
          __t(`${MSG_KEY}.sections.campaign_package.outfits_live_till_date`)
        )}
      </div>
    </div>
  );

  const renderPromotionBudgetSection = (): React.ReactNode => (
    <div className="ccm-campaign-details-overview-budget__budget-section">
      {renderTitle(__t(`${MSG_KEY}.sections.promotion_budget.title`))}
      <div className="ccm-campaign-details-overview-budget__budget-values">
        {renderBudgetItem(renderPrice(campaign.budget), __t(`${MSG_KEY}.sections.promotion_budget.budget_allocated`))}
        <div className="ccm-campaign-details-overview-budget__budget-separator" />
        {renderBudgetItem(renderPrice(campaign.budgetSpent), __t(`${MSG_KEY}.sections.promotion_budget.budget_spent`))}
        {renderBudgetItem(renderPrice(campaign.budgetRemaining), __t(`${MSG_KEY}.sections.promotion_budget.budget_remaining`))}
      </div>
    </div>
  );

  const renderProductionBudgetSection = (): React.ReactNode => (
    <div className="ccm-campaign-details-overview-budget__budget-section">
      {renderTitle(__t(`${MSG_KEY}.sections.production_budget.title`))}
      <div className="ccm-campaign-details-overview-budget__budget-values">
        {renderBudgetItem(renderPrice(campaign.contentProductionBudget), __t(`${MSG_KEY}.sections.production_budget.per_outfit`))}
        <div className="ccm-campaign-details-overview-budget__budget-separator" />
        {renderBudgetItem(campaign.liveOutfitsCount, __t(`${MSG_KEY}.sections.production_budget.outfits_live`))}
        {renderBudgetItem(renderPrice(campaign.totalContentProductionBudget), __t(`${MSG_KEY}.sections.production_budget.total_budget`))}
      </div>
    </div>
  );

  return (
    <div className="ccm-campaign-details-overview-budget">
      <div className="ccm-campaign-details-overview-budget__title">
        {__t(`${MSG_KEY}.${isCampaignCreatorless ? 'ccm_creatorless_title' : 'ccm_title'}`)}
        {
          !isCampaignCreatorless && (
            <Popover
              classNameWrapper="ccm-campaign-details-overview-budget__popover-wrapper"
              classNamePopover="ccm-campaign-details-overview-budget__popover"
              showQuestionMark
            >
              <div
                className="ccm-campaign-details-overview-budget__popover-contents"
                dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(getPopoverText()) }}
              />
            </Popover>
          )
        }
      </div>
      <div className="ccm-campaign-details-overview-budget__budgets">
        {isCampaignCreatorless ? renderCampaignPackage() : null}
        {renderPromotionBudgetSection()}
        {!isCampaignCreatorless ? renderProductionBudgetSection() : null}
      </div>
    </div>
  );
};

export default CcmCampaignDetailsOverviewBudget;
